import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Loader2, ShoppingCart, CircleDollarSign, Truck, PackageCheck, XCircle, ExternalLink, Building2, Package, FileText, AlertTriangle, RotateCcw } from 'lucide-react';
import { getTransactionDetail, downloadMarketplaceInvoice } from '@/lib/api';
import { fmt2 } from '@/lib/utils';
import { toast } from 'sonner';

const STEP_ICONS = {
  ordered: ShoppingCart,
  paid: CircleDollarSign,
  shipped: Truck,
  delivered: PackageCheck,
  cancelled: XCircle,
  refunded: RotateCcw,
  disputed: AlertTriangle,
};

const formatDate = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString('de-DE', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

export const OrderTimelineDialog = ({ open, onOpenChange, transactionId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloadingInvoice, setDownloadingInvoice] = useState(false);

  const handleDownloadInvoice = async () => {
    if (!transactionId) return;
    setDownloadingInvoice(true);
    try {
      const res = await downloadMarketplaceInvoice(transactionId);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Rechnung_${data?.invoice_number || data?.order_number || transactionId.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Rechnung heruntergeladen');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Rechnung konnte nicht geladen werden');
    } finally {
      setDownloadingInvoice(false);
    }
  };

  useEffect(() => {
    if (!open || !transactionId) return;
    let active = true;
    setLoading(true);
    getTransactionDetail(transactionId)
      .then((res) => { if (active) setData(res.data); })
      .catch(() => { if (active) setData(null); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [open, transactionId]);

  const timeline = data?.timeline || [];
  const isCancelled = data?.status === 'cancelled';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="order-timeline-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="h-5 w-5 text-orange-500" />
            Bestelldetails
          </DialogTitle>
          <DialogDescription>
            {data?.order_number ? (
              <span className="font-mono text-base font-semibold text-foreground" data-testid="order-number-detail">
                {data.order_number}
              </span>
            ) : 'Bestellnummer wird geladen…'}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin text-orange-500" />
          </div>
        ) : !data ? (
          <p className="text-center text-muted-foreground py-8">Daten konnten nicht geladen werden.</p>
        ) : (
          <div className="space-y-6">
            {/* Order summary */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 bg-muted/40 rounded-lg p-4 text-sm">
              <div>
                <p className="text-xs text-muted-foreground">Artikel</p>
                <p className="font-medium" data-testid="order-material-title">{data.material?.common_title || '—'}</p>
                {data.material?.manufacturer_name && (
                  <p className="text-xs text-muted-foreground">{data.material.manufacturer_name} {data.material.manufacturer_part_number || ''}</p>
                )}
              </div>
              <div className="text-right sm:text-left">
                <p className="text-xs text-muted-foreground">Menge × Wert</p>
                <p className="font-medium">
                  {data.qty || '—'} × {data.price_EUR ? `${fmt2(data.price_EUR)} EUR` : '—'}
                </p>
                {data.payment_total_EUR != null && (
                  <p className="text-xs text-muted-foreground">Gesamtbetrag: <span className="font-semibold text-foreground">{fmt2(data.payment_total_EUR)} EUR</span></p>
                )}
              </div>
              <div>
                <p className="text-xs text-muted-foreground flex items-center gap-1"><Building2 className="h-3 w-3" /> Verkäufer</p>
                <p className="font-medium">{data.seller_group?.group_name || '—'}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground flex items-center gap-1"><Building2 className="h-3 w-3" /> Käufer</p>
                <p className="font-medium">{data.buyer_group?.group_name || '—'}</p>
              </div>
            </div>

            {/* Stepper timeline */}
            <div className="relative" data-testid="order-timeline-stepper">
              {timeline.map((step, idx) => {
                const Icon = STEP_ICONS[step.key] || ShoppingCart;
                const isLast = idx === timeline.length - 1;
                const isCurrentStep = !isCancelled && step.completed && (idx === timeline.length - 1 || !timeline[idx + 1]?.completed);
                const toneByKey = {
                  cancelled: 'bg-red-500 text-white border-red-500',
                  refunded: 'bg-purple-500 text-white border-purple-500',
                  disputed: 'bg-amber-500 text-white border-amber-500',
                };
                const tone = step.completed
                  ? (toneByKey[step.key] || 'bg-orange-500 text-white border-orange-500')
                  : 'bg-background text-muted-foreground border-border';
                const connectorTone = step.completed && timeline[idx + 1]?.completed
                  ? 'bg-orange-500'
                  : 'bg-border';

                return (
                  <div key={step.key} className="flex gap-4 pb-6 relative" data-testid={`timeline-step-${step.key}`}>
                    {/* Vertical connector */}
                    {!isLast && (
                      <div className={`absolute left-[19px] top-10 w-0.5 h-full ${connectorTone}`} />
                    )}
                    <div className={`shrink-0 w-10 h-10 rounded-full border-2 flex items-center justify-center ${tone}`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="flex-1 pt-1">
                      <div className="flex flex-wrap items-baseline justify-between gap-2">
                        <h4 className={`font-semibold ${step.completed ? 'text-foreground' : 'text-muted-foreground'}`}>
                          {step.label}
                          {isCurrentStep && (
                            <Badge className="ml-2 bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400 text-[10px]">
                              Aktuell
                            </Badge>
                          )}
                        </h4>
                        {step.completed_at && (
                          <span className="text-xs text-muted-foreground font-medium" data-testid={`timeline-time-${step.key}`}>
                            {formatDate(step.completed_at)}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">{step.description}</p>
                      {step.key === 'paid' && step.completed && (
                        <div className="mt-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs"
                            onClick={handleDownloadInvoice}
                            disabled={downloadingInvoice}
                            data-testid="download-invoice-timeline-btn"
                          >
                            {downloadingInvoice ? (
                              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                            ) : (
                              <FileText className="h-3 w-3 mr-1" />
                            )}
                            Rechnung {data?.invoice_number ? `(${data.invoice_number})` : ''} herunterladen
                          </Button>
                        </div>
                      )}
                      {step.key === 'shipped' && step.completed && step.extra && (
                        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs">
                          {step.extra.carrier && (
                            <span>Spediteur: <span className="font-medium text-foreground">{step.extra.carrier}</span></span>
                          )}
                          {step.extra.tracking_number && (
                            <span>Sendungsnr.: <span className="font-mono font-medium text-foreground">{step.extra.tracking_number}</span></span>
                          )}
                          {step.extra.delivery_note_url && (
                            <a href={step.extra.delivery_note_url} target="_blank" rel="noreferrer" className="text-orange-600 hover:underline flex items-center gap-0.5">
                              <ExternalLink className="h-3 w-3" /> Lieferschein
                            </a>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
